import { getCalendarBenefit, getCurrency, getPartialFeatures } from '@mkp/shared/util-product';
import {
  CREDIT_PUBLICATION_DAYS_OVERRIDES,
  Product,
  PRODUCT_TRANSLATIONS,
  ProductInformation,
} from '@mkp/shared/models';
import { ProductDto } from './product.dto';

/**
 * Enriches a product from the API with the translations, features and calendar the
 * client holds for its code.
 *
 * Returns undefined when the client cannot enrich the code — a product Ecom serves
 * before the client knows it, or one whose variants carry no price. One unmappable
 * product must not empty the whole list for its caller, so callers filter.
 */
export const toViewModel = (productDto: ProductDto): Product | undefined => {
  const { id, code, mainTaxon, variants } = productDto;

  const translations = PRODUCT_TRANSLATIONS[code];
  const basePrice = variants[0]?.prices[0]?.price;
  const features = getPartialFeatures({ code });
  const calendar = features && getCalendarBenefit(features);

  if (!translations || !features || !calendar || basePrice === undefined) {
    console.warn(`Failed to transform product ${code}`);
    return undefined;
  }

  return {
    id,
    code,
    name: translations.name,
    slug: code.toLowerCase(),
    mainTaxon,
    variants,

    features,
    calendar,
    longname: translations.longname,
    shortName: translations.shortname,
    currency: getCurrency({ code, variants }),
    description: translations.description,
    basePrice,
  };
};

/**
 * The simplified product metadata the credit workflows use.
 *
 * Returns undefined when the product code is unknown to the client, for the same
 * reason as `toViewModel`.
 */
export const toProductInformation = ({
  code,
  name,
}: ProductDto): ProductInformation | undefined => {
  const features = getPartialFeatures({ code });
  const calendar = features && getCalendarBenefit(features);

  if (!calendar) {
    console.warn(`Failed to transform product information ${code}`);
    return undefined;
  }

  return {
    type: code,
    publicationDays: CREDIT_PUBLICATION_DAYS_OVERRIDES[code] ?? calendar.period,
    name: { de: name, en: name, fr: name },
  };
};
